import type { ReactNode } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import type { CardStub } from '@/features/dashboard/api/schemas'
import { useFilteredCard } from '@/features/dashboard/filters/useFilteredCard'
import { chartTotals } from '@/features/dashboard/format/format'
import { toFigure } from '@/features/dashboard/format/figure'
import {
  cardTransitionName,
  tileButtonId,
  viewTransitionStyle,
} from '@/shared/lib/viewTransition'
import { CardFigure } from './CardFigure'
import { EmptyCardMessage } from './EmptyCardMessage'
import { InfoTip } from './InfoTip'
import { Sparkline } from '../charts/Sparkline'
import { chartSummary, valueSummary } from '../charts/lib/chartSummary'
import styles from './ModuleTile.module.css'

type TileShellProps = {
  stub: CardStub
  onExpand?: ((fromKeyboard: boolean) => void) | undefined
  /** Showing the previous numbers while new ones load. */
  busy?: boolean
  /** The headline value, read out with the expand button's name. */
  summary?: string | null | undefined
  children: ReactNode
}

const summaryId = (cardId: number) => `tile-summary-${cardId}`

// The whole tile is one click target: an invisible button stretched over it,
// beneath the (i), which stays its own button. It comes after the content so
// it paints over anything transformed (the odometer digits), which would
// otherwise swallow the click.
const TileShell = ({
  stub,
  onExpand,
  busy,
  summary,
  children,
}: TileShellProps) => (
  <article
    className={`${styles.tile} ${onExpand ? styles.interactive : ''} ${busy ? styles.busy : ''}`}
    aria-busy={busy || undefined}
    style={viewTransitionStyle(cardTransitionName(stub.cardId))}
  >
    <div className={styles.header}>
      <h3 className={styles.title} title={stub.name}>
        {stub.name}
      </h3>
      {stub.description && (
        <InfoTip
          name={stub.name}
          description={stub.description}
          className={styles.info}
        />
      )}
    </div>
    {children}
    {onExpand && summary && (
      <VisuallyHidden id={summaryId(stub.cardId)}>{summary}</VisuallyHidden>
    )}
    {onExpand && (
      <button
        id={tileButtonId(stub.cardId)}
        type='button'
        className={styles.expand}
        aria-label={`Show ${stub.name} in detail`}
        aria-describedby={summary ? summaryId(stub.cardId) : undefined}
        // A key press clicks with a detail of 0.
        onClick={event => onExpand(event.detail === 0)}
      />
    )}
  </article>
)

type ModuleTileProps = {
  stub: CardStub
  /** `fromKeyboard`: the tile was activated with a key rather than clicked. */
  onExpand: (fromKeyboard: boolean) => void
}

export const ModuleTile = ({ stub, onExpand }: ModuleTileProps) => {
  const {
    data: card,
    isPending,
    isError,
    isRefreshing,
    clearMarkets,
  } = useFilteredCard(stub.cardId)

  if (isPending) {
    return (
      <TileShell stub={stub}>
        <div className={styles.skeleton} role='status'>
          <VisuallyHidden>Loading</VisuallyHidden>
        </div>
      </TileShell>
    )
  }

  if (isError) {
    return (
      <TileShell stub={stub}>
        <p className={styles.error} role='alert'>
          Couldn&apos;t load this card.
        </p>
      </TileShell>
    )
  }

  if (card.kind === 'empty') {
    // Nothing to open: the only action is to clear the filters.
    return (
      <article
        className={`${styles.emptyTile} ${isRefreshing ? styles.busy : ''}`}
        aria-label={stub.name}
        aria-busy={isRefreshing || undefined}
        style={viewTransitionStyle(cardTransitionName(stub.cardId))}
      >
        <EmptyCardMessage reason={card.reason} onClear={clearMarkets} />
      </article>
    )
  }

  const figure = toFigure(card)

  return (
    <TileShell
      stub={stub}
      onExpand={onExpand}
      busy={isRefreshing}
      summary={valueSummary(card)}
    >
      {figure && (
        <CardFigure figure={figure} size='tile' className={styles.values} />
      )}
      {card.kind === 'chart' && card.categories.length > 0 && (
        <div className={styles.chart}>
          <Sparkline
            name={card.name}
            label={chartSummary(card)}
            categories={card.categories}
            data={chartTotals(card)}
          />
        </div>
      )}
    </TileShell>
  )
}
